<script setup>
// NOTICE: KAPTION-INTEGRATION
// security-center.UI.7 — Dominios verificados (canvas: Main › Dominios, Domain).
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionSecurityAPI from 'dashboard/api/kaptionSecurity';
import { useKaptionSecurity } from 'dashboard/composables/useKaptionSecurity';
import KaptionSecuritySection from '../components/KaptionSecuritySection.vue';
import KaptionSecurityBadge from '../components/KaptionSecurityBadge.vue';
import KaptionSecurityCopyRow from '../components/KaptionSecurityCopyRow.vue';
import { useKaptionSecurityText } from '../useKaptionSecurityText';

const { t } = useI18n();
const { overview, domains, fetchOverview } = useKaptionSecurity();
const { formatRelative } = useKaptionSecurityText(overview);

const addRef = ref(null);
const verifyRef = ref(null);
const removeRef = ref(null);
const newDomain = ref('');
const addError = ref('');
const busy = ref(false);
const current = ref(null);
const lastCheck = ref(null);

const openAdd = () => {
  newDomain.value = '';
  addError.value = '';
  addRef.value?.open();
};

const openVerify = domain => {
  current.value = domain;
  lastCheck.value = domain.last_check_result || null;
  verifyRef.value?.open();
};

const add = async () => {
  busy.value = true;
  addError.value = '';
  try {
    const { data } = await KaptionSecurityAPI.addDomain(newDomain.value.trim());
    addRef.value?.close();
    await fetchOverview({ force: true });
    openVerify(data);
  } catch (e) {
    const reason = (e?.response?.data?.error_codes?.domain || [])[0] || '';
    const known = [
      'consumer',
      'public_suffix',
      'invalid',
      'limit_reached',
      'taken',
    ];
    addError.value = known.includes(reason)
      ? t(`KAPTION_SECURITY.DOMAINS.ERRORS.${reason}`)
      : t('KAPTION_SECURITY.DOMAINS.ERRORS.generic');
  } finally {
    busy.value = false;
  }
};

const check = async () => {
  if (!current.value) return;
  busy.value = true;
  try {
    const { data } = await KaptionSecurityAPI.checkDomain(current.value.id);
    current.value = data;
    lastCheck.value = data.check;
    await fetchOverview({ force: true });
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};

const askRemove = domain => {
  current.value = domain;
  removeRef.value?.open();
};

const remove = async () => {
  busy.value = true;
  try {
    await KaptionSecurityAPI.removeDomain(current.value.id);
    removeRef.value?.close();
    useAlert(t('KAPTION_SECURITY.DOMAINS.REMOVED'));
    await fetchOverview({ force: true });
  } catch (e) {
    useAlert(t('KAPTION_SECURITY.COMMON.ERROR'));
  } finally {
    busy.value = false;
  }
};

const verifyNote = computed(() => {
  const domain = current.value;
  if (!domain) return null;
  if (domain.status === 'verified')
    return {
      tone: 'bg-n-teal-3 text-n-teal-11',
      text: t('KAPTION_SECURITY.DOMAINS.DONE', { domain: domain.domain }),
    };
  if (lastCheck.value === 'taken')
    return {
      tone: 'bg-n-ruby-3 text-n-ruby-11',
      text: t('KAPTION_SECURITY.DOMAINS.TAKEN'),
    };
  if (domain.last_checked_at)
    return {
      tone: 'bg-n-amber-3 text-n-amber-11',
      text: t('KAPTION_SECURITY.DOMAINS.NOT_FOUND', {
        when: formatRelative(domain.last_checked_at),
      }),
    };
  return {
    tone: 'bg-n-alpha-2 text-n-slate-11',
    text: t('KAPTION_SECURITY.DOMAINS.NEVER_CHECKED'),
  };
});
</script>

<template>
  <div class="flex flex-col gap-11">
    <KaptionSecuritySection
      :title="t('KAPTION_SECURITY.DOMAINS.TITLE')"
      :description="t('KAPTION_SECURITY.DOMAINS.DESC')"
    >
      <template #actions>
        <Button
          sm
          type="button"
          :label="t('KAPTION_SECURITY.DOMAINS.ADD')"
          data-testid="ks-domain-add"
          @click="openAdd"
        />
      </template>
      <p v-if="!domains.length" class="m-0 text-sm text-n-slate-11">
        {{ t('KAPTION_SECURITY.DOMAINS.EMPTY') }}
      </p>
      <div v-else class="border rounded-xl border-n-weak">
        <div
          v-for="(domain, index) in domains"
          :key="domain.id"
          class="flex flex-wrap items-center gap-3 px-4 py-3.5"
          :class="{ 'border-t border-n-weak': index > 0 }"
          :data-testid="`ks-domain-${domain.domain}`"
        >
          <span class="flex-1 text-sm font-semibold text-n-slate-12">{{
            domain.domain
          }}</span>
          <KaptionSecurityBadge
            :tone="domain.status === 'verified' ? 'teal' : 'amber'"
            :label="
              domain.status === 'verified'
                ? t('KAPTION_SECURITY.DOMAINS.VERIFIED')
                : t('KAPTION_SECURITY.DOMAINS.PENDING')
            "
          />
          <span
            v-if="domain.status === 'verified'"
            class="text-[13px] text-n-slate-11"
          >
            {{
              t('KAPTION_SECURITY.DOMAINS.PEOPLE', {
                count: domain.people_count,
              })
            }}
          </span>
          <button
            v-else
            type="button"
            class="text-[13px] font-medium text-n-brand"
            @click="openVerify(domain)"
          >
            {{ t('KAPTION_SECURITY.DOMAINS.SHOW_RECORD') }}
          </button>
          <button
            type="button"
            class="text-[13px] text-n-ruby-11"
            @click="askRemove(domain)"
          >
            {{ t('KAPTION_SECURITY.DOMAINS.REMOVE') }}
          </button>
        </div>
      </div>
    </KaptionSecuritySection>

    <Dialog
      ref="addRef"
      :title="t('KAPTION_SECURITY.DOMAINS.ADD_TITLE')"
      :description="t('KAPTION_SECURITY.DOMAINS.ADD_DESC')"
      :confirm-button-label="t('KAPTION_SECURITY.DOMAINS.ADD_SUBMIT')"
      :disable-confirm-button="!newDomain.trim() || busy"
      :is-loading="busy"
      @confirm="add"
    >
      <div class="flex flex-col gap-1.5">
        <label
          for="ks-new-domain"
          class="text-sm font-medium text-n-slate-12"
          >{{ t('KAPTION_SECURITY.DOMAINS.ADD_LABEL') }}</label
        >
        <input
          id="ks-new-domain"
          v-model="newDomain"
          type="text"
          autocomplete="off"
          class="h-10 px-3 text-sm border rounded-xl border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :placeholder="t('KAPTION_SECURITY.DOMAINS.ADD_PLACEHOLDER')"
        />
        <span v-if="addError" class="text-sm text-n-ruby-11">{{
          addError
        }}</span>
      </div>
    </Dialog>

    <Dialog
      ref="verifyRef"
      :title="
        current
          ? t('KAPTION_SECURITY.DOMAINS.VERIFY_TITLE', {
              domain: current.domain,
            })
          : ''
      "
      :description="t('KAPTION_SECURITY.DOMAINS.VERIFY_DESC')"
      :confirm-button-label="t('KAPTION_SECURITY.DOMAINS.CHECK_NOW')"
      :cancel-button-label="t('KAPTION_SECURITY.DOMAINS.CLOSE_LATER')"
      :show-confirm-button="current?.status !== 'verified'"
      :is-loading="busy"
      width="xl"
      @confirm="check"
    >
      <div
        v-if="current"
        class="flex flex-col gap-4"
        data-testid="ks-domain-dialog"
      >
        <div
          class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
        >
          <KaptionSecurityCopyRow
            :label="t('KAPTION_SECURITY.DOMAINS.TYPE')"
            :value="current.record_type"
          />
          <KaptionSecurityCopyRow
            :label="t('KAPTION_SECURITY.DOMAINS.NAME')"
            :value="current.record_name"
          />
          <KaptionSecurityCopyRow
            :label="t('KAPTION_SECURITY.DOMAINS.VALUE')"
            :value="current.record_value"
          />
        </div>
        <div
          v-if="verifyNote"
          class="px-3 py-2.5 text-sm rounded-lg"
          :class="verifyNote.tone"
        >
          {{ verifyNote.text }}
        </div>
        <p class="m-0 text-[13px] text-n-slate-11">
          {{ t('KAPTION_SECURITY.DOMAINS.ONE_ACCOUNT') }}
        </p>
      </div>
    </Dialog>

    <Dialog
      ref="removeRef"
      type="alert"
      :title="
        current
          ? t('KAPTION_SECURITY.DOMAINS.REMOVE_TITLE', {
              domain: current.domain,
            })
          : ''
      "
      :description="
        current
          ? t('KAPTION_SECURITY.DOMAINS.REMOVE_DESC', {
              domain: current.domain,
            })
          : ''
      "
      :confirm-button-label="t('KAPTION_SECURITY.DOMAINS.REMOVE')"
      :is-loading="busy"
      @confirm="remove"
    />
  </div>
</template>
